import { userProfileMock, fullUserProfileMock } from '../schema/api3/mocks/userProfileMock';

export const makeProfileObject = (userProfile: typeof userProfileMock) => {
  const {
    id: profileId,
    appointments: appointments,
    primary: isPrimary,
    firstname,
    lastname,
    preferred_name: preferredName,
    avatar,
    email,
    dob,
    gender,
    mobile_phone: mobilePhone,
    home_phone: homePhone,
    work_phone: workPhone,
    home_address: homeAddress,
    postal_address: postalAddress,
    phi,
  } = userProfile;

  const { street: homeStreet, suburb: homeSuburb, state: homeState, postcode: homePostcode } =
    homeAddress ?? {};

  const { street: postStreet, suburb: postSuburb, state: postState, postcode: postPostcode } =
    postalAddress ?? {};

  return {
    appointments: appointments ?? undefined,
    profileId: profileId ?? undefined,
    isPrimary: isPrimary ?? undefined,
    firstName: firstname ?? undefined,
    lastName: lastname ?? undefined,
    preferredName: preferredName ?? undefined,
    avatar: avatar ?? undefined,
    email: email?.value ?? undefined,
    emailVerified: email?.verified ?? undefined,
    dateOfBirth: dob ?? undefined,
    gender: gender ?? undefined,
    mobilePhoneNumber: mobilePhone?.number ?? undefined,
    mobilePhoneVerified: mobilePhone?.verified ?? undefined,
    homePhoneNumber: homePhone?.number ?? undefined,
    workPhoneNumber: workPhone?.number ?? undefined,
    homeAddress: {
      street: homeStreet ?? undefined,
      suburb: homeSuburb ?? undefined,
      state: homeState ?? undefined,
      postcode: homePostcode ?? undefined,
    },
    postalAddress: {
      street: postStreet ?? undefined,
      suburb: postSuburb ?? undefined,
      state: postState ?? undefined,
      postcode: postPostcode ?? undefined,
    },
    phi: {
      provider: phi?.provider ?? undefined,
      number: phi?.number ?? undefined,
      isMct: phi?.is_mct ?? undefined,
      mctConsent: phi?.mct_consent ?? undefined,
    },
  };
};

export const makeFullProfileObject = (userProfile: typeof fullUserProfileMock) => {
  const basicProfile = makeProfileObject(userProfile);

  const {
    medicare,
    occupation,
    marital_status: maritalStatus,
    pension_card: pensionCard,
    health_care_card: healthCareCard,
    dva,
    emergency_contact: emergencyContact,
    end_of_life_care: endOfLifeCare,
    cultural,
    allergies,
    medicines,
    next_of_kin: nextOfKin,
  } = userProfile;

  const {
    background,
    country_of_birth: countryOfBirth,
    interpreter_required: interpreterRequired,
    interpreter_language: interpreterLanguage,
  } = cultural || {};

  const {
    firstname: nextOfKinFirstName,
    lastname: nextOfKinLastName,
    relation: nextOfKinRelation,
    phone_mobile: nextOfKinMobilePhone,
    phone_home: nextOfKinHomePhone,
    phone_work: nextOfKinWorkPhone,
  } = nextOfKin || {};

  const {
    firstname: emergencyContactFirstName,
    lastname: emergencyContactLastName,
    relation: emergencyContactRelation,
    phone_mobile: emergencyContactMobilePhone,
    phone_home: emergencyContactHomePhone,
    phone_work: emergencyContactWorkPhone,
  } = emergencyContact || {};

  return {
    ...basicProfile,
    occupation: occupation ?? undefined,
    medicare: {
      number: medicare?.number ?? undefined,
      irn: medicare?.irn ?? undefined,
      expiry: medicare?.expiry ?? undefined,
    },
    maritalStatus: maritalStatus ?? undefined,
    pensionCard: {
      number: pensionCard?.number ?? undefined,
      expiry: pensionCard?.expiry ?? undefined,
    },
    healthCareCard: {
      number: healthCareCard?.number ?? undefined,
      expiry: healthCareCard?.expiry ?? undefined,
    },
    dva: {
      number: dva?.number ?? undefined,
      expiry: dva?.expiry ?? undefined,
    },
    endOfLifeCare: endOfLifeCare ?? undefined,
    cultural: {
      culturalBackground: background ?? undefined,
      countryOfBirth: countryOfBirth ?? undefined,
      interpreterRequired: interpreterRequired ?? undefined,
      interpreterLanguage: interpreterLanguage ?? undefined,
    },
    allergies: allergies?.map(allergy => ({
      name: allergy.allergy ?? undefined,
      reaction: allergy.description ?? undefined,
    })),
    medicines: medicines?.map(medicine => ({
      name: medicine.name ?? undefined,
      dose: medicine.dosage ?? undefined,
      frequency: medicine.frequency ?? undefined,
    })),
    emergencyContact: {
      firstName: emergencyContactFirstName ?? undefined,
      lastName: emergencyContactLastName ?? undefined,
      relation: emergencyContactRelation ?? undefined,
      mobilePhone: emergencyContactMobilePhone ?? undefined,
      homePhone: emergencyContactHomePhone ?? undefined,
      workPhone: emergencyContactWorkPhone ?? undefined,
    },
    nextOfKin: {
      firstName: nextOfKinFirstName ?? undefined,
      lastName: nextOfKinLastName ?? undefined,
      relation: nextOfKinRelation ?? undefined,
      mobilePhone: nextOfKinMobilePhone ?? undefined,
      homePhone: nextOfKinHomePhone ?? undefined,
      workPhone: nextOfKinWorkPhone ?? undefined,
    },
  };
};
